/**
 * Marker appearance for the Direct Mail canvassing map.
 *
 * Icons are inline SVG data URIs rather than hosted images: the app's CSP only
 * allows same-origin and a short allowlist of hosts, so a remote marker sprite
 * would be blocked, and a data URI also removes a per-marker network request
 * when a neighborhood renders a few hundred pins at once.
 *
 * Colors are exported separately from the icon factories because the page's
 * legend has to draw the same swatches without touching the Maps API.
 */

import type { MailerPinStatus } from '../../types/directMail';

export const PIN_STATUS_COLORS: Record<MailerPinStatus, string> = {
  pinned: '#0EA5E9',
  queued: '#F59E0B',
  measuring: '#F97316',
  measured: '#8B5CF6',
  approved: '#22C55E',
  batched: '#0891B2',
  mailed: '#2563EB',
  responded: '#15803D',
  skipped: '#9E9E9E',
};

/** A pin that exists only in the browser — geocoding or saving is still in flight. */
export const DRAFT_PIN_COLOR = '#94A3B8';

/** A house the org may not mail: do-not-knock, duplicate, existing customer. */
export const SUPPRESSED_PIN_COLOR = '#DC2626';

function toIcon(svg: string, size: number): google.maps.Icon {
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new google.maps.Size(size, size),
    anchor: new google.maps.Point(size / 2, size / 2),
  };
}

function circleSvg(body: string): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 30 30">${body}</svg>`;
}

function ring(selected: boolean): { stroke: string; width: number; radius: number; size: number } {
  return selected
    ? { stroke: '#111827', width: 4, radius: 11, size: 32 }
    : { stroke: '#FFFFFF', width: 3, radius: 9, size: 24 };
}

export function statusPinIcon(status: MailerPinStatus, selected: boolean): google.maps.Icon {
  const { stroke, width, radius, size } = ring(selected);
  const color = PIN_STATUS_COLORS[status] ?? DRAFT_PIN_COLOR;
  return toIcon(
    circleSvg(
      `<circle cx="15" cy="15" r="${radius}" fill="${color}" stroke="${stroke}" stroke-width="${width}"/>`
    ),
    size
  );
}

/** Dashed outline reads as "not committed yet" while the pin is being saved. */
export function draftPinIcon(selected: boolean): google.maps.Icon {
  const { stroke, width, radius, size } = ring(selected);
  return toIcon(
    circleSvg(
      `<circle cx="15" cy="15" r="${radius}" fill="${DRAFT_PIN_COLOR}" fill-opacity="0.7" ` +
        `stroke="${stroke}" stroke-width="${width}" stroke-dasharray="4 3"/>`
    ),
    size
  );
}

/** Suppressed houses stay visible so the rep can see *why* nothing was created. */
export function suppressedPinIcon(selected: boolean): google.maps.Icon {
  const { stroke, width, radius, size } = ring(selected);
  return toIcon(
    circleSvg(
      `<circle cx="15" cy="15" r="${radius}" fill="${SUPPRESSED_PIN_COLOR}" stroke="${stroke}" stroke-width="${width}"/>` +
        `<path d="M11.5 11.5 L18.5 18.5 M18.5 11.5 L11.5 18.5" stroke="#FFFFFF" stroke-width="2.5" stroke-linecap="round"/>`
    ),
    size
  );
}
